/**
 * Credit Settlement – Settlement Report.
 * GET /histopathology/reports/credit-settlement/?from_date&to_date[&credit_type&corporate_name&patient_name&bill_number&export=pdf]
 * Header totals: rows_count, settlements_count, bills_count, bill_amount, settled_amount, received_amount, tds_amount, balance_amount.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, Tooltip, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 1.2, py: 0.9, minWidth: "8vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1.1vw", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.15 }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.85vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function SettlementReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const creditType = "corporate";
  const [search, setSearch] = useState("");
  const [totals, setTotals] = useState({
    rows_count: 0, settlements_count: 0, bills_count: 0,
    bill_amount: 0, settled_amount: 0, received_amount: 0,
    tds_amount: 0, balance_amount: 0,
  });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    if (creditType) params.append("credit_type", creditType);
    return params.toString();
  }, [fromDate, toDate, creditType]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_CREDIT_SETTLEMENT}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          const t = res.data.totals || {};
          setTotals({
            rows_count: +t.rows_count || 0,
            settlements_count: +t.settlements_count || 0,
            bills_count: +t.bills_count || 0,
            bill_amount: +t.bill_amount || 0,
            settled_amount: +t.settled_amount || 0,
            received_amount: +t.received_amount || 0,
            tds_amount: +t.tds_amount || 0,
            balance_amount: +t.balance_amount || 0,
          });
        } else {
          setRows([]);
          setTotals({
            rows_count: 0, settlements_count: 0, bills_count: 0,
            bill_amount: 0, settled_amount: 0, received_amount: 0,
            tds_amount: 0, balance_amount: 0,
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.patient_name, r.patient_account_no, r.patient_mobile, r.bill_number, r.nxt_id, r.corporate_name, r.credit_type]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleExportPdf = () => {
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_CREDIT_SETTLEMENT}?${buildQuery()}&export=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to export PDF.", ok: false, okText: "" }));
  };

  const columns = [
    {
      field: "patient_details", headerName: "Patient's Details", flex: 1.6, minWidth: 220, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ textTransform: "uppercase" }}>{p.row.patient_name || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[p.row.patient_account_no, p.row.patient_mobile].filter(Boolean).join(" | ") || "-"}
          </Typography>
        </Box>
      ),
    },
    { field: "settlement_date", headerName: "Payment Date", flex: 1.1, minWidth: 150 },
    {
      field: "credit_type", headerName: "Credit Type", flex: 0.9, minWidth: 110,
      renderCell: (p) => {
        const raw = (p.row.credit_type || "").toString();
        const display = raw.toLowerCase() === "corporate" ? "Hospital" : (raw || "-");
        return (
          <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", overflow: "hidden" }}>
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{display}</Typography>
            {p.row.corporate_name && (
              <Tooltip title={p.row.corporate_name} placement="top" arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {p.row.corporate_name}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "description", headerName: "Description", flex: 1.4, minWidth: 180, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{p.row.bill_number || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[p.row.nxt_id, p.row.bill_date].filter(Boolean).join(" | ") || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "bill_amount", headerName: `Total Amount (${CurrencySymbol})`, flex: 1, minWidth: 120,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.bill_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.bill_amount ?? 0, false),
    },
    {
      field: "settled_amount", headerName: `Settled Amount (${CurrencySymbol})`, flex: 1, minWidth: 120,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.settled_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.settled_amount ?? 0, false),
    },
    {
      field: "received_amount", headerName: `Received Amount (${CurrencySymbol})`, flex: 1, minWidth: 130,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.received_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.received_amount ?? 0, false),
    },
    {
      field: "tds_amount", headerName: `TDS (${CurrencySymbol})`, flex: 0.8, minWidth: 90,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.tds_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.tds_amount ?? 0, false),
    },
    {
      field: "balance_amount", headerName: `Balance (${CurrencySymbol})`, flex: 0.9, minWidth: 110,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.balance_amount ?? 0),
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.85vw", color: +p.row.balance_amount > 0 ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}>
          {CommonValidation.AmountFormat(p.row.balance_amount ?? 0, false)}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <SummaryCard label="Total Amount" value={CommonValidation.AmountFormat(totals.bill_amount)} />
        <SummaryCard label="Settled Amount" value={CommonValidation.AmountFormat(totals.settled_amount)} />
        <SummaryCard label="Received Amount" value={CommonValidation.AmountFormat(totals.received_amount)} />
        <SummaryCard label="TDS" value={CommonValidation.AmountFormat(totals.tds_amount)} />
        <SummaryCard label="Balance" value={CommonValidation.AmountFormat(totals.balance_amount)} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "10vw" }}>
          <CommonDatePicker
            FromDate={fromDate} ToDate={toDate} DateIndex={dateIndex}
            HideAllMenu removeMarginRight
            SelectedDates={(from, to, idx) => { setFromDate(from); setToDate(to); setDateIndex(idx); }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "11vw" }} />
        <Tooltip title="Export PDF">
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportPdf} sx={{ textTransform: "capitalize" }}>
            PDF
          </Button>
        </Tooltip>
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.settlement_detail_id ?? r.id ?? `row-${i}` }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={56}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
